import { Search } from "@mui/icons-material";
import { InputAdornment } from "@mui/material";
import React, { FC, useMemo } from "react";
import { Control } from "react-hook-form";
import {
  FormController,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
} from "../../../../../../components";
import { MAX_LENGTH_75 } from "../../../../../../helpers/constants";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  ExistingDeliverableFilterFormProps,
  ExistingDeliverableFormKey,
  ExistingDeliverablesType,
} from "../../../../modelsAndSchemas/Models";
import { createProgramSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { ExistingDeliverableWrapper } from "./ExistingDeliverables.style";

export interface ExistingDeliverableProps {
  onHeaderCheckBoxChange: (checked: boolean) => void;
  isAllSelected: boolean;
  pageId: string;
  handleRowCheckboxChange: (checked: boolean, id: string) => void;
  existingFilterFormControl: Control<ExistingDeliverableFilterFormProps>;
  tableRef: React.RefObject<{ resetPagination: () => void }>;
  existingDeliverablesList: ExistingDeliverablesType[];
  noDataInApi: boolean;
}

interface HeaderCheckboxProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

const HeaderCheckbox: FC<HeaderCheckboxProps & { fetchLabel: (key: string, subKey: string) => string }> = ({
  checked,
  onCheckedChange,
  fetchLabel
}) => {
  const { commonARIALabel } = useNavigationSiteCore();
  
  return (
    <MuiCheckbox
      checked={checked}
      checkboxLabel={`${commonARIALabel.selectAll} ${fetchLabel(
        createProgramSiteCoreKeys.createDeliverableKey,
        createProgramSiteCoreKeys.deliverableName
      )}`}
      hideLabel={true}
      onCheckedChange={onCheckedChange}
    />
  );
};

export const getExistingDeliverablesColumns = (
  isAllSelected: boolean,
  onHeaderCheckBoxChange: (checked: boolean) => void,
  handleRowCheckboxChange: (checked: boolean, id: string) => void,
  pageId: string,
  fetchLabel: (key: string, subKey: string) => string,
) => [
  {
    id: ExistingDeliverableFormKey.isSelected,
    label: "",
    className: "checkbox-cell",
    isSortVisible: false,
    headerRender: () => (
      <HeaderCheckbox
        checked={isAllSelected}
        onCheckedChange={onHeaderCheckBoxChange}
        fetchLabel={fetchLabel}
      />
    ),
    render: (row: any) => (
      <MuiCheckbox
        checked={row.isSelected}
        checkboxLabel={row.name}
        hideLabel={true}
        id={`${pageId}-${row.id}`}
        onCheckedChange={(checked: boolean) =>
          handleRowCheckboxChange(checked, row.id)
        }
      />
    ),
  },
  {
    id: ExistingDeliverableFormKey.name,
    label: fetchLabel(
      createProgramSiteCoreKeys.createDeliverableKey,
      createProgramSiteCoreKeys.deliverableName,
    ),
    isSortVisible: true,
    className: "cell-name",
  },
  {
    id: ExistingDeliverableFormKey.deliverableTypeName,
    label: fetchLabel(
      createProgramSiteCoreKeys.createDeliverableKey,
      createProgramSiteCoreKeys.deliverableType,
    ),
    isSortVisible: true,
  },
  {
    id: ExistingDeliverableFormKey.unitName,
    label: fetchLabel(
      createProgramSiteCoreKeys.createDeliverableKey,
      createProgramSiteCoreKeys.createDeliverableUnits,
    ),
    isSortVisible: true,
  },
  {
    id: ExistingDeliverableFormKey.measurement,
    label: fetchLabel(
      createProgramSiteCoreKeys.createDeliverableKey,
      createProgramSiteCoreKeys.createDeliverableMeasurement,
    ),
    isSortVisible: true,
  },
];

const ExistingDeliverables: FC<ExistingDeliverableProps> = ({
  onHeaderCheckBoxChange,
  isAllSelected,
  pageId,
  handleRowCheckboxChange,
  existingFilterFormControl,
  tableRef,
  existingDeliverablesList,
  noDataInApi,
}) => {
  const { commonLabels, fetchDropdownValues, fetchLabel } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const columns = useMemo(
    () =>
      getExistingDeliverablesColumns(
        isAllSelected,
        onHeaderCheckBoxChange,
        handleRowCheckboxChange,
        pageId,
        fetchLabel,
      ),
    [isAllSelected, onHeaderCheckBoxChange, handleRowCheckboxChange, pageId],
  );
  const renderFormControl = (
    control: any,
    name: keyof typeof ExistingDeliverableFormKey,
    options: OptionsDataType[],
    placeholder: string,
    id: string,
  ) => (
    <FormController control={control} name={name} key={name}>
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={options}
        value=""
        id={id}
        placeholder={placeholder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>
  );

  const tableHeaderForm = [
    <></>,
    <FormController
      control={existingFilterFormControl}
      name={ExistingDeliverableFormKey.name}
      key={ExistingDeliverableFormKey.name}
    >
      <MuiTextField
        id={`${pageId}-${ExistingDeliverableFormKey.name}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_75}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    renderFormControl(
      existingFilterFormControl,
      ExistingDeliverableFormKey.deliverableType,
      fetchDropdownValues(
        createProgramSiteCoreKeys.createDeliverableKey,
        createProgramSiteCoreKeys.deliverableTypeList,
      ),
      commonLabels.selectPlaceHolder,
      `${pageId}-${ExistingDeliverableFormKey.deliverableType}`,
    ),
    renderFormControl(
      existingFilterFormControl,
      ExistingDeliverableFormKey.units,
      fetchDropdownValues(
        createProgramSiteCoreKeys.createDeliverableKey,
        createProgramSiteCoreKeys.createDeliverableUnitList,
      ),
      commonLabels.selectPlaceHolder,
      `${pageId}-${ExistingDeliverableFormKey.units}`,
    ),
    <></>,
  ];
  return (
    <ExistingDeliverableWrapper>
      <MuiGrid container className="existing-header">
        <MuiGrid size={{ xs: 6 }}>
          <MuiTypography variant="h2SemiBold" component="h2">
            {fetchLabel(
              createProgramSiteCoreKeys.createDeliverableKey,
              createProgramSiteCoreKeys.existingDeliverables,
            )}
          </MuiTypography>
        </MuiGrid>
      </MuiGrid>

      <MuiBox className="table-container">
        <MuiGrid container data-testid="existing-deliverables">
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={tableHeaderForm}
              columns={columns}
              tableResults={[...existingDeliverablesList]}
              noDataInApi={noDataInApi}
              noResultMessage={commonLabels.noResult}
              emptyMessage={commonLabels.noResult}
              priorityResults={[]}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
    </ExistingDeliverableWrapper>
  );
};

export default ExistingDeliverables;
